import {Address} from '../../../common/address.js'
import apiverson from '../../../common/apiverson.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import TextInput from '../../../common/components/TextInput.js'
import {getUnique} from '../../../common/getUnique.js'
import {buildPath} from '../../../common/querystring.js'
import {isPresent} from '../../../common/utils.js'
import {companySelector} from '../../../data/company.js'
import {
  contactSelector,
  contactsSelector,
  setContact,
} from '../../../data/contacts.js'
import {
  formSelector,
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
  useSelector,
} from '../../../store.js'
import {showMessageToast} from '../../Header/Toast/index.js'

const MODAL_FORM = 'add_edit_contact_modal'

const defaultNameRegEx = /^Contact (\d+)$/
function getUniqueContactName() {
  const presets = contactsSelector(getState())

  return getUnique(
    'Contact ',
    defaultNameRegEx,
    Object.values(presets).map(({nickname}) => nickname),
  )
}

export function showAddEditContactModal(contactID) {
  const company = companySelector(getState())
  const contact = contactID
    ? contactSelector(getState(), {contactID})
    : {
        nickname: getUniqueContactName(),
        address: new Address({email: company.email}),
      }

  setForm(MODAL_FORM, {
    contactID,
    nickname: contact.nickname,
    ...contact.address,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formSelector(state, {formName: MODAL_FORM})
}

export function errorsSelector(state) {
  const {nickname, email} = modalFormSelector(state)
  const errors = {}

  if (!isPresent(nickname)) {
    errors.nickname = 'Nickname is required'
    errors.preventSave = true
  }

  if (!isPresent(email)) {
    errors.email = 'Email is required'
    errors.preventSave = true
  }

  return errors
}

async function saveContact() {
  try {
    updateModalForm({serverError: null, isSaving: true})

    const {contactID, nickname, email, email_cc, email_bcc} =
      modalFormSelector(getState())

    const params = {
      nickname: nickname.trim(),
      address: {
        email: email.trim(),
        email_cc: email_cc.trim(),
        email_bcc: email_bcc.trim(),
      },
    }

    const {json} = contactID
      ? await apiverson.patch(buildPath(['contact', contactID]), params)
      : await apiverson.post(buildPath(['contact']), params)

    setContact(json)

    showMessageToast(
      `Contact was successfully ${contactID ? 'Saved' : 'Created'}.`,
    )

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function AddEditContactModal({form}) {
  const errors = useSelector(errorsSelector)

  return (
    <ConfirmModal
      title={`${form.contactID ? 'Save' : 'Create'} Contact`}
      isSaving={form.isSaving}
      confirmText={form.contactID ? 'Save' : 'Create'}
      cancelText="Cancel"
      onConfirm={() => saveContact()}
      onCancel={() => closeModal()}
      error={form.serverError}
      isDisabled={errors.preventSave}
    >
      <div className="row">
        <div className="columns medium-8">
          <ul className="list list--form margin-bottom-10">
            <li className="list__item--form margin-bottom-15">
              <TextInput
                label="Nickname"
                id="nickname"
                className="input--med-h"
                required
                value={form.nickname}
                onChange={(nickname) => updateModalForm({nickname})}
                errorMessage={errors.nickname}
                autoFocus
              />
            </li>
            <li className="list__item--form margin-bottom-15">
              <TextInput
                label="Email"
                id="email"
                className="input--med-h"
                required
                value={form.email}
                onChange={(email) =>
                  updateModalForm({
                    email,
                  })
                }
                errorMessage={errors.email}
              />
            </li>
            <li className="list__item--form margin-bottom-15">
              <TextInput
                label="Email CC"
                id="email_cc"
                className="input--med-h"
                value={form.email_cc}
                onChange={(email_cc) =>
                  updateModalForm({
                    email_cc,
                  })
                }
                errorMessage={errors.email_cc}
              />
            </li>
            <li className="list__item--form margin-bottom-0">
              <TextInput
                label="Email BCC"
                id="email_bcc"
                className="input--med-h"
                value={form.email_bcc}
                onChange={(email_bcc) =>
                  updateModalForm({
                    email_bcc,
                  })
                }
                errorMessage={errors.email_bcc}
              />
            </li>
          </ul>
        </div>
      </div>
    </ConfirmModal>
  )
}

export default onlyIfForm(AddEditContactModal, modalFormSelector)
